import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL;

export default function Chat({ session, onLogout }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const sendMessage = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text };
    const history = [...messages, userMsg];
    setMessages(history);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(
        `${API_BASE}/chat`,
        { messages: history.map(({ role, content }) => ({ role, content })) },
        { headers: { Authorization: session.token }, timeout: 90000 }
      );
      setMessages([...history, {
        role: 'assistant',
        content: res.data.response,
        signature: res.data.signature || null,
        toolTrace: res.data.toolTrace || [],
      }]);
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Something went wrong. Please try again.';
      setMessages([...history, { role: 'assistant', content: `⚠️ ${errMsg}` }]);
    } finally {
      setLoading(false);
      textareaRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  const autoResize = (e) => {
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
  };

  return (
    <div style={s.container}>
      {/* Header */}
      <header style={s.header}>
        <div style={s.headerLeft}>
          <div style={s.headerIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="10" rx="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <div>
            <div style={s.headerTitle}>Agentic AI Chat</div>
            <div style={s.headerSub}>Challenger MCP · AgentCore Gateway · Bedrock</div>
          </div>
        </div>
        <div style={s.headerRight}>
          <span style={s.userEmail}>{session.email}</span>
          <button style={s.logoutBtn} onClick={onLogout}>Sign out</button>
        </div>
      </header>

      {/* Messages */}
      <div style={s.messagesArea}>
        {messages.length === 0 && (
          <div style={s.welcome}>
            <div style={s.welcomeIcon}>🔏</div>
            <h2 style={s.welcomeTitle}>Agentic signing assistant</h2>
            <p style={s.welcomeText}>
              Ask me anything — or ask me to sign a challenge. Try:{' '}
              <code style={s.codeHint}>sign this challenge: hello-world-123</code>
            </p>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} style={{ ...s.messageRow, ...(msg.role === 'user' ? s.messageRowUser : {}) }}>
            <div style={msg.role === 'user' ? s.avatarUser : s.avatarAssistant}>
              {msg.role === 'user' ? '👤' : '✦'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: 0 }}>
              <div style={msg.role === 'user' ? s.bubbleUser : s.bubbleAssistant}>
                {msg.content}
              </div>
              {msg.toolTrace && msg.toolTrace.length > 0 && (
                <div style={s.toolTrace}>
                  {msg.toolTrace.map((t, j) => (
                    <span key={j} style={s.toolBadge}>
                      🔧 {t.error ? 'failed' : 'called'} <b>{t.tool}</b> via MCP gateway
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div style={{ ...s.messageRow }}>
            <div style={s.avatarAssistant}>✦</div>
            <div style={s.bubbleAssistant}>
              <span style={s.typingDot} />
              <span style={{ ...s.typingDot, animationDelay: '0.2s' }} />
              <span style={{ ...s.typingDot, animationDelay: '0.4s' }} />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={s.inputArea}>
        <form style={s.form} onSubmit={sendMessage}>
          <textarea
            ref={textareaRef}
            style={s.textarea}
            value={input}
            onChange={e => { setInput(e.target.value); autoResize(e); }}
            onKeyDown={handleKeyDown}
            placeholder='Message — e.g. "sign this challenge: abc123" (Enter to send)'
            rows={1}
            autoFocus
          />
          <button style={{ ...s.sendBtn, ...((!input.trim() || loading) ? s.sendBtnDisabled : {}) }}
            type="submit" disabled={!input.trim() || loading}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </form>
        <p style={s.hint}>Signatures are produced by the Challenger MCP signing service (KMS-backed) via AgentCore Gateway.</p>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-5px); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

const s = {
  container: {
    display: 'flex', flexDirection: 'column',
    height: '100vh', maxWidth: '860px',
    margin: '0 auto', width: '100%',
  },
  header: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.9rem 1.5rem',
    background: '#161929',
    borderBottom: '1px solid #2a3050',
    flexShrink: 0,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  headerIcon: {
    width: '36px', height: '36px',
    background: '#7c6eff', borderRadius: '10px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff',
  },
  headerTitle: { fontWeight: '600', fontSize: '0.95rem' },
  headerSub: { fontSize: '0.72rem', color: '#8892a8', marginTop: '1px' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  userEmail: { fontSize: '0.8rem', color: '#8892a8' },
  logoutBtn: {
    padding: '0.35rem 0.75rem',
    background: 'transparent', border: '1px solid #2a3050',
    borderRadius: '6px', color: '#8892a8',
    fontSize: '0.78rem', cursor: 'pointer',
  },
  messagesArea: {
    flex: 1, overflowY: 'auto',
    padding: '1.5rem 1.5rem 0.5rem',
    display: 'flex', flexDirection: 'column', gap: '1.25rem',
  },
  welcome: {
    textAlign: 'center',
    padding: '4rem 1rem',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    margin: 'auto 0',
  },
  welcomeIcon: { fontSize: '2.5rem', marginBottom: '1rem' },
  welcomeTitle: { fontSize: '1.4rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.5rem' },
  welcomeText: { fontSize: '0.9rem', color: '#8892a8', maxWidth: '420px' },
  codeHint: {
    background: '#1e2237', border: '1px solid #2a3050',
    borderRadius: '4px', padding: '0.1rem 0.35rem',
    fontSize: '0.8rem', color: '#9d93ff',
  },
  messageRow: {
    display: 'flex', gap: '0.75rem',
    alignItems: 'flex-start',
    animation: 'fadeUp 0.2s ease',
    maxWidth: '88%',
  },
  messageRowUser: { alignSelf: 'flex-end', flexDirection: 'row-reverse' },
  avatarUser: {
    width: '30px', height: '30px',
    background: '#7c6eff', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.75rem', flexShrink: 0,
  },
  avatarAssistant: {
    width: '30px', height: '30px',
    background: '#1e2237', border: '1px solid #2a3050',
    borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.85rem', color: '#7c6eff', flexShrink: 0,
  },
  bubbleUser: {
    padding: '0.75rem 1rem',
    background: '#7c6eff', borderRadius: '16px 16px 4px 16px',
    color: '#fff', fontSize: '0.9rem', lineHeight: '1.6',
    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  },
  bubbleAssistant: {
    padding: '0.75rem 1rem',
    background: '#1e2237', border: '1px solid #2a3050',
    borderRadius: '16px 16px 16px 4px',
    color: '#e2e8f0', fontSize: '0.9rem', lineHeight: '1.6',
    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap',
  },
  toolTrace: { display: 'flex', flexWrap: 'wrap', gap: '0.35rem' },
  toolBadge: {
    fontSize: '0.7rem', color: '#8892a8',
    background: '#161929', border: '1px solid #2a3050',
    borderRadius: '20px', padding: '0.2rem 0.6rem',
  },
  typingDot: {
    display: 'inline-block',
    width: '7px', height: '7px',
    background: '#8892a8', borderRadius: '50%',
    animation: 'bounce 1.2s infinite ease-in-out',
  },
  inputArea: {
    padding: '1rem 1.5rem 1.25rem',
    background: '#161929', borderTop: '1px solid #2a3050',
    flexShrink: 0,
  },
  form: { display: 'flex', gap: '0.65rem', alignItems: 'flex-end' },
  textarea: {
    flex: 1, padding: '0.75rem 1rem',
    background: '#1e2237', border: '1px solid #2a3050',
    borderRadius: '12px', color: '#e2e8f0',
    fontSize: '0.9rem', fontFamily: 'inherit',
    resize: 'none', outline: 'none',
    minHeight: '48px', lineHeight: '1.5',
    transition: 'border-color 0.2s',
  },
  sendBtn: {
    width: '48px', height: '48px', flexShrink: 0,
    background: '#7c6eff', border: 'none',
    borderRadius: '12px', color: '#fff',
    cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    transition: 'background 0.2s, transform 0.1s',
  },
  sendBtnDisabled: { opacity: 0.45, cursor: 'not-allowed' },
  hint: { fontSize: '0.72rem', color: '#4a5270', textAlign: 'center', marginTop: '0.6rem' },
};
